import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type {
  MiembroFamiliar,
  MiembroFamiliarCreatePayload,
  MiembroFamiliarUpdatePayload,
  Parentesco,
} from '../types/afiliado';

const QK = {
  byAfiliado: (afiliadoId: string) => ['grupo_familiar', afiliadoId] as const,
  parentescos: ['parentescos'] as const,
};

// ----- LIST por afiliado -----

async function fetchGrupoFamiliar(afiliadoId: string): Promise<MiembroFamiliar[]> {
  const { data, error } = await supabase
    .from('grupo_familiar')
    .select('*')
    .eq('afiliado_id', afiliadoId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as MiembroFamiliar[];
}

export function useGrupoFamiliar(afiliadoId: string | undefined) {
  return useQuery({
    queryKey: afiliadoId ? QK.byAfiliado(afiliadoId) : ['grupo_familiar', 'none'],
    queryFn: () => fetchGrupoFamiliar(afiliadoId!),
    enabled: !!afiliadoId,
  });
}

// ----- CREATE -----

export function useCreateMiembroFamiliar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MiembroFamiliarCreatePayload) => {
      const { data, error } = await supabase
        .from('grupo_familiar')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as MiembroFamiliar;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
    },
  });
}

// ----- UPDATE -----

export function useUpdateMiembroFamiliar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: MiembroFamiliarUpdatePayload;
    }) => {
      const { data, error } = await supabase
        .from('grupo_familiar')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MiembroFamiliar;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
    },
  });
}

// ----- DELETE -----

export function useDeleteMiembroFamiliar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, afiliado_id }: { id: string; afiliado_id: string }) => {
      const { error } = await supabase.from('grupo_familiar').delete().eq('id', id);
      if (error) throw error;
      return { id, afiliado_id };
    },
    onSuccess: ({ afiliado_id }) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(afiliado_id) });
    },
  });
}

// ----- Catalogo: parentescos -----

export function useParentescos() {
  return useQuery({
    queryKey: QK.parentescos,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parentescos')
        .select('*')
        .order('descripcion');
      if (error) throw error;
      return (data ?? []) as Parentesco[];
    },
    staleTime: 5 * 60_000, // catalogo, cambia poco
  });
}
